import { attr, belongsTo, hasMany } from '@ember-data/model';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import moment from 'moment-timezone';
import BaseModel from 'volta/models/base-model';
import TimeBufferAlerts from 'volta/models/constants/time-buffer-alerts';
import { collectionCommand, resourceCommand as command } from 'volta/utils/api';
import { indexByFn } from 'volta/utils/array-utils';
import { formatDurationInHours, formatISODate } from 'volta/utils/date-utils';
import { parseNumber } from 'volta/utils/math-utils';
import {
  colorForAlertCriticality,
  colorForCategory,
  getBufferZoneCriticality
} from 'volta/utils/style-utils';
import { hashCode, sanitized } from 'volta/utils/text-utils';
import { WorkshopTypes } from './workshop';

import type { IntlService } from 'ember-intl';
import type { IResourceName } from 'volta/models/base-model';
import type { TTimeBufferAlert } from 'volta/models/constants/time-buffer-alerts';
import type { TCustomFieldValues } from 'volta/models/types/custom-field-definition';
import type { TRouteType } from 'volta/models/types/route-operation';
import type { ISupplierIdentityInfo } from 'volta/models/types/supplier';
import type { IWarehouseIdentityInfo } from 'volta/models/types/warehouse';
import type { ICmdResult } from 'volta/utils/api/jsonapi-types';
import type { THue } from 'volta/utils/style-utils';
import type AppSettings from 'volta/services/app-settings';
import type { TAcquisitionCode } from './constants/acquisition-codes';
import type { TPOField } from './po-group';
import type {
  IAddOperation,
  IChangeManyRoadmaps,
  IChangeManyStatus,
  IChangeOneStatus,
  IChangeRoadmap,
  ICommitManyCmd,
  ICreateDraftOrder,
  ICreateDraftOrderFromSku,
  IDraftProjections,
  IDraftPlanningProjections,
  IFinishOperation,
  IManyPlanningOrdersUpdatedResult,
  IPlanningOrderSKU,
  IPOCmd,
  IPOWorkshopInfo,
  IRemoveOperation,
  ISchedulingAlertSummary,
  IStartOperation,
  ISyncAlertSummary,
  IToggleOperationReschedulingLock,
  IUpdateExternalData,
  IUpdateManyCmd,
  IUpdateManyOperations,
  IUpdateOperation,
  IUpdateOrderNum,
  IUpdatePlanningOrder,
  IValidateSupplyAlternativeProposals,
  TDateField,
  TPOPriorityOption,
  TPOProposedValues,
  TPOStatus,
  TPOStatusOption,
  TPOSyncStatus,
  TPOValidationStatus,
  TPOValidationStatusOption,
  TSyncAlerts
} from './types/planning-order';
import type { IAggTasksViewMap } from './types/tasks';
import type { IWorkshopIdentityInfo } from './types/workshop';

export interface IPOKeyObject {
  orderHash: string;
  orderKey: string;
  num?: string;
  warehouse: IWarehouseIdentityInfo;
  acqCode: TAcquisitionCode;
  supplier?: ISupplierIdentityInfo;
  workshop?: IWorkshopIdentityInfo;
  deliveryWorkshop?: IWorkshopIdentityInfo;
  externalOrderNum?: string;
}

export const SyncAlerts: Record<TSyncAlerts, number> = {
  CRITICAL: 0,
  WARNING: 1,
  NONE: 2
};

export const TPoNewStatuses: TPOStatus[] = ['NEW', 'PROJECTED'];

export const SyncAlertOptions = (Object.keys(SyncAlerts) as TSyncAlerts[]).map((k: TSyncAlerts) => {
  return {
    key: k,
    value: SyncAlerts[k],
    label: `syncAlerts.${k}`,
    icon: 'sync-outline',
    color: colorForCategory(k)
  };
});

export const TPOSyncStatusArray: TPOSyncStatus[] = ['SYNCED', 'ERROR', 'SYNCING'] as const;

export const SyncStatusOptions: IStatusOption<TPOSyncStatus>[] = [
  {
    index: 0,
    key: 'SYNCED',
    value: 'SYNCED',
    label: 'syncStatuses.SYNCED',
    color: 'green'
  },
  {
    index: 1,
    key: 'ERROR',
    value: 'ERROR',
    label: 'syncStatuses.ERROR',
    color: 'red'
  },
  {
    index: 2,
    key: 'SYNCING',
    value: 'SYNCING',
    label: 'syncStatuses.SYNCING',
    color: 'blue'
  }
];

export const SchedulingAlertOptions = [0, 1, 2, 3].map((k: number) => {
  return {
    key: k,
    value: k,
    label: `poWorkbench.alerts.scheduling.${k}.title`,
    icon: 'time-outline',
    color: colorForAlertCriticality(k)
  };
});

export const StatusesArray: TPOStatusOption[] = (
  [
    ['NEW', 'gray', 0],
    ['DRAFT', 'orange', 0],
    ['LAUNCHED', 'blue', 25],
    ['COMMITTED', 'indigo', 50],
    ['SENT', 'purple', 75],
    ['FINISHED', 'gray', 100],
    ['CANCELLED', 'red', 0],
    ['PROJECTED', 'teal', 0],
    ['PROJECTED_FIRM', 'blue', 0]
  ] as [TPOStatus, THue, number][]
).map(([value, color, progress]: [TPOStatus, THue, number], index: number) => ({
  index: index + 1,
  value,
  key: value,
  color,
  progress,
  label: `planningOrderStatuses.${value}`
}));

export const POStatusOptions: Record<TPOStatus, TPOStatusOption> = indexByFn(
  StatusesArray,
  (s) => s.value
);

export const getPOStatusOption = (key?: string) => {
  return key ? POStatusOptions[key as TPOStatus] : undefined;
};

const getPoValidationStatusLabelKey = (status: TPOValidationStatus, isSup: boolean) =>
  `poValidationStatuses.${isSup ? 'supplier' : 'customer'}.${status}`;

export const POValidationStatusesArray: TPOValidationStatusOption[] = (
  [
    ['CUSTOMER_PROPOSAL', 'red', 0, false],
    ['CUSTOMER_REFUSAL', 'red', undefined, true],
    ['CUSTOMER_APPROVAL', 'green', 100, false],
    ['SUPPLIER_DRAFT', 'orange', 25, false],
    ['SUPPLIER_PROPOSAL', 'purple', 50, false],
    ['AUTO_VALIDATED', 'green', 100, false]
  ] as [TPOValidationStatus, THue, number, boolean][]
).map(
  (
    [value, color, progress, isError]: [TPOValidationStatus, THue, number, boolean],
    index: number
  ) => ({
    index: index + 1,
    key: value,
    value,
    color,
    progress,
    isError,
    label: undefined,
    customerLabel: getPoValidationStatusLabelKey(value, false),
    supplierLabel: getPoValidationStatusLabelKey(value, true)
  })
);

export const POValidationStatuses: Record<TPOValidationStatus, TPOValidationStatusOption> =
  indexByFn(POValidationStatusesArray, (a) => a.value);

export const getPOValidationStatusOption = (key?: string) => {
  return key ? POValidationStatuses[key as TPOValidationStatus] : undefined;
};

const HiddenStatuses = new Set([
  POStatusOptions.FINISHED.value,
  POStatusOptions.CANCELLED.value,
  POStatusOptions.PROJECTED_FIRM.value
]);

export const FirmPOStatuses: TPOStatus[] = [
  POStatusOptions.LAUNCHED.value,
  POStatusOptions.FINISHED.value,
  POStatusOptions.COMMITTED.value,
  POStatusOptions.CANCELLED.value,
  POStatusOptions.SENT.value
];

export const SupplierPortalStatuses: TPOStatus[] = [
  POStatusOptions.LAUNCHED.value,
  POStatusOptions.COMMITTED.value,
  POStatusOptions.SENT.value
];

export const VisibleStatusOptions: TPOStatusOption[] = StatusesArray.filter(
  (s) => !HiddenStatuses.has(s.value)
);

export const POResourceName: IResourceName = {
  singular: 'planningOrder',
  plural: 'planningOrders'
};

export const OperationResourceName: IResourceName = {
  singular: 'operation',
  plural: 'operations'
};

export const Commands = {
  // One
  AcceptOneSupplierProposal: 'AcceptOneSupplierProposal',
  ChangeRoadmap: 'ChangeRoadmap',
  CommitOne: 'CommitOne',
  CreateDraftOrder: 'CreateDraftOrder',
  CreateDraftOrderFromSku: 'CreateDraftOrderFromSku',
  DeleteOne: 'DeleteOne',
  DraftOne: 'DraftOne',
  FinishOne: 'FinishOne',
  LaunchOne: 'LaunchOne',
  RefuseOneSupplierProposal: 'RefuseOneSupplierProposal',
  SendOne: 'SendOne',
  SendOneSupplierProposal: 'SendOneSupplierProposal',
  SplitOne: 'SplitOne',
  UpdateExternalData: 'UpdateExternalData',
  UpdateOrderNum: 'UpdateOrderNum',
  UpdatePlanningOrder: 'UpdatePlanningOrder',
  //Operations
  AddOperation: 'AddOperation',
  RemoveOperation: 'RemoveOperation',
  StartOperation: 'StartOperation',
  FinishOperation: 'FinishOperation',
  ToggleOperationReschedulingLock: 'ToggleOperationReschedulingLock',
  UpdateOperation: 'UpdateOperation',
  UpdateManyOperations: 'UpdateManyOperations',
  // Many
  AcceptManySupplierProposals: 'AcceptManySupplierProposals',
  RefuseManySupplierProposals: 'RefuseManySupplierProposals',
  ChangeManyRoadmaps: 'ChangeManyRoadmaps',
  CommitMany: 'CommitMany',
  DeleteMany: 'DeleteMany',
  DraftPlanningProjections: 'DraftPlanningProjections',
  DraftProjections: 'DraftProjections',
  ExplodeMany: 'ExplodeMany',
  FinishMany: 'FinishMany',
  LaunchMany: 'LaunchMany',
  SendMany: 'SendMany',
  SendManySupplierProposals: 'SendManySupplierProposals',
  UpdateMany: 'UpdateMany',
  // Supply Alternative Proposals
  ValidateSupplyAlternativeProposals: 'ValidateSupplyAlternativeProposals'
};

export const POEvents = {
  // One
  PlanningOrderCancelled: 'PlanningOrderCancelled',
  PlanningOrderCreated: 'PlanningOrderCreated',
  PlanningOrderDeleted: 'PlanningOrderDeleted',
  PlanningOrderExternalDataChanged: 'PlanningOrderExternalDataChanged',
  PlanningOrderFinished: 'PlanningOrderFinished',
  PlanningOrderRoadmapChanged: 'PlanningOrderRoadmapChanged',
  PlanningOrderCommitted: 'PlanningOrderCommitted',
  PlanningOrderSent: 'PlanningOrderSent',
  PlanningOrderLaunched: 'PlanningOrderLaunched',
  PlanningOrderSplit: 'PlanningOrderSplit',
  PlanningOrderSupplierProposed: 'PlanningOrderSupplierProposed',
  PlanningOrderSupplierDrafted: 'PlanningOrderSupplierDrafted',
  PlanningOrderSupplierSplit: 'PlanningOrderSupplierSplit',
  PlanningOrderSupplierProposalRefused: 'PlanningOrderSupplierProposalRefused',
  PlanningOrderUpdated: 'PlanningOrderUpdated',
  // Many
  PlanningOrdersCommitted: 'PlanningOrdersCommitted',
  PlanningOrdersDeleted: 'PlanningOrdersDeleted',
  PlanningOrdersDrafted: 'PlanningOrdersDrafted',
  PlanningOrdersSplit: 'PlanningOrdersSplit',
  PlanningOrderRoadmapsChanged: 'PlanningOrderRoadmapsChanged',
  PlanningOrdersExternalDataChanged: 'PlanningOrdersExternalDataChanged',
  PlanningOrdersFinished: 'PlanningOrdersFinished',
  PlanningOrdersLaunched: 'PlanningOrdersLaunched',
  PlanningOrdersOrderNumChanged: 'PlanningOrdersOrderNumChanged',
  PlanningOrdersSent: 'PlanningOrdersSent',
  PlanningOrdersSupplierDrafted: 'PlanningOrdersSupplierDrafted',
  PlanningOrdersSupplierProposed: 'PlanningOrdersSupplierProposed',
  PlanningOrdersSupplierProposalsRefused: 'PlanningOrdersSupplierProposalsRefused',
  PlanningOrdersUpdated: 'PlanningOrdersUpdated',
  // Operations
  POOperationAdded: 'POOperationAdded',
  POOperationRemoved: 'POOperationRemoved',
  POOperationStarted: 'POOperationStarted',
  POOperationFinished: 'POOperationFinished',
  OperationsUpdated: 'OperationsUpdated',
  POOperationUpdated: 'POOperationUpdated'
};

export const POLifecycleEvents = [
  POEvents.PlanningOrderCommitted,
  POEvents.PlanningOrderSplit,
  POEvents.PlanningOrderExternalDataChanged,
  POEvents.PlanningOrderLaunched,
  POEvents.PlanningOrderSent,
  POEvents.PlanningOrderSupplierDrafted,
  POEvents.PlanningOrderSupplierProposed,
  POEvents.PlanningOrderSupplierProposalRefused,
  POEvents.PlanningOrderSupplierSplit,
  POEvents.PlanningOrderUpdated,
  POEvents.PlanningOrderRoadmapChanged,
  POEvents.POOperationAdded,
  POEvents.POOperationRemoved,
  POEvents.POOperationStarted,
  POEvents.POOperationFinished,
  POEvents.POOperationUpdated
];

export const POsLifecycleEvents = [
  POEvents.PlanningOrdersCommitted,
  POEvents.PlanningOrdersDrafted,
  POEvents.PlanningOrdersSplit,
  POEvents.PlanningOrdersExternalDataChanged,
  POEvents.PlanningOrdersLaunched,
  POEvents.PlanningOrdersOrderNumChanged,
  POEvents.PlanningOrdersSent,
  POEvents.PlanningOrdersSupplierDrafted,
  POEvents.PlanningOrdersSupplierProposed,
  POEvents.PlanningOrdersSupplierProposalsRefused,
  POEvents.PlanningOrdersUpdated,
  POEvents.PlanningOrderRoadmapsChanged,
  POEvents.OperationsUpdated
];

export const POsSupplierEvents = [
  POEvents.PlanningOrdersSupplierDrafted,
  POEvents.PlanningOrdersSupplierProposed,
  POEvents.PlanningOrdersSupplierProposalsRefused
];

export const POSupplierEvents = [
  POEvents.PlanningOrderSupplierProposed,
  POEvents.PlanningOrderSupplierDrafted,
  POEvents.PlanningOrderSupplierSplit,
  POEvents.PlanningOrderSupplierProposalRefused
];

export const PODeletedEvents = [
  POEvents.PlanningOrderDeleted,
  POEvents.PlanningOrderCancelled,
  POEvents.PlanningOrderFinished
];

export const POCreatedEvents = [
  POEvents.PlanningOrderCreated,
  POEvents.PlanningOrderRoadmapChanged,
  POEvents.PlanningOrderSent,
  POEvents.PlanningOrderSplit,
  POEvents.PlanningOrderCommitted,
  POEvents.PlanningOrderUpdated
];

export const POQueries = {
  Users: 'users',
  WorkshopsCapacity: 'workshopsCapacity'
};

export const POPriorityOptions: TPOPriorityOption[] = [
  {
    key: 'blocker',
    label: 'blocker',
    icon: 'remove-circle',
    iconColor: 'red',
    value: 5
  },
  {
    key: 'critical',
    label: 'critical',
    icon: 'reorder-four',
    iconColor: 'red',
    value: 4
  },
  {
    key: 'high',
    label: 'high',
    icon: 'reorder-three',
    iconColor: 'orange',
    value: 3
  },
  {
    key: 'medium',
    label: 'medium',
    icon: 'reorder-two',
    iconColor: 'yellow',
    value: 2
  },
  {
    key: 'low',
    label: 'low',
    icon: 'remove',
    iconColor: 'green',
    value: 1
  },
  {
    key: 'trivial',
    label: 'trivial',
    icon: 'ellipse-outline',
    iconColor: 'gray',
    value: 0
  },
  {
    key: 'none',
    label: 'none',
    icon: 'flag-outline',
    iconColor: 'gray',
    value: 'none'
  }
];

export const POSortMapping: Partial<Record<TPOField, string[]>> = {
  orderNum: ['num'],
  executionBuffer: ['executionInfo.ohaZone', 'executionInfo.oha'],
  planningBuffer: ['planningInfo.bufferZone', 'planningInfo.bufferPenetration'],
  operationStarted: ['operationStarted'],
  schedulingAlert: ['alerts.scheduling.criticality'],
  syncAlert: ['alerts.synchronisation.criticality'],
  timeBufferAlert: ['timeBufferAlertOption.index'],
  sku: ['skuInfo.part.code'],
  part: ['skuInfo.part.code'],
  supplier: ['supplierInfo.code'],
  warehouse: ['skuInfo.warehouse.code'],
  workshop: ['workshopInfo.code'],
  partType: ['skuInfo.partType.code'],
  deliveryWorkshop: ['deliveryWorkshopInfo.code']
};

const POFieldMapping: Record<TPOField, (po: PlanningOrder) => unknown> = {
  'sku.customFields': (po) => po.skuInfo.customFields,
  abcClass: (po: PlanningOrder) => po.skuInfo.skuStats?.abcClass,
  acqCode: (po) => po.acqCode,
  cancelledAt: (po) => po.cancelledAt,
  committedAt: (po) => po.committedAt,
  createdBy: (po) => po.createdBy,
  currency: (po) => po.currency,
  customFields: (po: PlanningOrder) => po.customFields,
  deliveryDate: (po) => po.deliveryDate,
  deliveryWorkshop: (po) => po.deliveryWorkshopId,
  dueDate: (po) => po.effectiveDueDate,
  estimatedEndDate: (po) => po.estimatedEndDate,
  estimatedEndTime: (po) => po.estimatedEndTime,
  estimatedProcessTime: (po) => po.estimatedProcessTime,
  estimatedStartTime: (po) => po.estimatedStartTime,
  executionBuffer: (po) => po.executionInfo.ohaZone,
  externalOrderNum: (po) => po.externalOrderNum,
  finishedAt: (po) => po.finishedAt,
  g: (po) =>
    po.skuInfo.supplierSkuDescription +
    '__' +
    po.skuInfo.part.description +
    '__' +
    po.skuInfo.part.code,
  groupId: (po) => po.groupId,
  id: (po) => po.id,
  isOperation: (po) => po.isOperation,
  launchDate: (po) => po.launchDate,
  launchedAt: (po) => po.launchedAt,
  leadTimeCategory: (po: PlanningOrder) => po.skuInfo.skuStats?.leadTimeCategory,
  methodology: (po: PlanningOrder) => po.skuInfo.methodology,
  nextOperationNum: (po) => po.nextOperationNum,
  operationCustomFields: (po) => po.operationCustomFields,
  operationName: (po) => po.operationName,
  operationNum: (po) => po.operationNum,
  operationStarted: (po) => po.operationStarted,
  orderNum: (po) => po.num,
  orderType: (po) => po.orderType,
  parentPoId: (po) => po.parentPoId,
  part: (po) => po.skuInfo.part.partId,
  partType: (po) => po.skuInfo.partType?.partTypeId,
  planningBuffer: (po) => po.planningInfo.bufferZone,
  planningId: (po) => po.planningId,
  priority: (po) => po.priority,
  processCompletion: (po) => po.processCompletion,
  processPcb: (po) => po.processPcb,
  processTime: (po) => po.processTime,
  reorderDate: (po) => po.reorderDate,
  reorderQty: (po) => po.reorderQty,
  reorderStrategy: (po: PlanningOrder) => po.skuInfo.reorderStrategy,
  resourceQty: (po) => po.resourceQty,
  routeType: (po) => po.routeType,
  schedulingAlert: (po) => po.alerts.scheduling?.criticality,
  sentAt: (po) => po.sentAt,
  setupPcb: (po) => po.setupPcb,
  setupTime: (po) => po.setupTime,
  sku: (po) => po.skuId,
  startedAt: (po) => po.startedAt,
  status: (po) => po.status,
  supplier: (po) => po.supplierId,
  syncAlert: (po) => po.alerts.synchronisation?.criticality,
  syncStatus: (po) => po.syncStatus,
  timeBufferAlert: (po) => po.timeBufferAlert,
  totalPrice: (po) => po.formattedTotalPrice,
  unitPrice: (po) => po.formattedUnitPrice,
  validationStatus: (po) => po.validationStatus,
  varCategory: (po: PlanningOrder) => po.skuInfo.skuStats?.varCategory,
  warehouse: (po) => po.warehouseId,
  workshop: (po) => po.workshopId,
  xyzClass: (po: PlanningOrder) => po.skuInfo.skuStats?.xyzClass
};

export const POPriorityOptionsDict = indexByFn(POPriorityOptions, (o) => o.value.toString());
const noId = 'zzzzzzzz-zzzz-zzzz-zzzz-zzzzzzzzzzzz';
const noOrderNum = '00000000';

export default class PlanningOrder extends BaseModel {
  @service intl!: IntlService;
  @service appSettings!: AppSettings;
  // TODO - Remove when investigation on
  // why it only disappears on Planning Order model is done
  static modelName = 'planning-order';

  /**
   * Collection commands
   */

  static createDraftOrderFromSku = collectionCommand<
    ICreateDraftOrderFromSku,
    PlanningOrder,
    typeof PlanningOrder
  >(Commands.CreateDraftOrderFromSku);
  static draftProjections = collectionCommand<
    IDraftProjections,
    PlanningOrder[],
    typeof PlanningOrder
  >(Commands.DraftProjections);

  static draftPlanningProjections = collectionCommand<
    IDraftPlanningProjections,
    PlanningOrder[],
    typeof PlanningOrder
  >(Commands.DraftPlanningProjections);

  static validateSupplyAlternativeProposals = collectionCommand<
    IValidateSupplyAlternativeProposals,
    ICmdResult<PlanningOrder[]>,
    typeof PlanningOrder
  >(Commands.ValidateSupplyAlternativeProposals);

  static createDraftOrder = collectionCommand<
    ICreateDraftOrder,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.CreateDraftOrder);

  static changeManyRoadmaps = collectionCommand<
    IChangeManyRoadmaps,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.ChangeManyRoadmaps);

  static launchMany = collectionCommand<
    IChangeManyStatus,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.LaunchMany);

  static commitMany = collectionCommand<
    ICommitManyCmd,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.CommitMany);

  static sendMany = collectionCommand<
    IChangeManyStatus,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.SendMany);

  static finishMany = collectionCommand<
    IChangeManyStatus,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.FinishMany);

  static deleteMany = collectionCommand<
    IChangeManyStatus,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.DeleteMany);

  static explodeMany = collectionCommand<
    IChangeManyStatus,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.ExplodeMany);

  static updateOrderNum = collectionCommand<
    IUpdateOrderNum,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.UpdateOrderNum);

  static updateExternalData = collectionCommand<
    IUpdateExternalData,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.UpdateExternalData);

  static acceptManySupplierProposals = collectionCommand<
    IChangeManyStatus,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.AcceptManySupplierProposals);

  static refuseManySupplierProposals = collectionCommand<
    IChangeManyStatus,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.RefuseManySupplierProposals);

  static sendManySupplierProposals = collectionCommand<
    IChangeManyStatus,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.SendManySupplierProposals);

  static updateMany = collectionCommand<
    IUpdateManyCmd,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.UpdateMany);

  static updateManyOperations = collectionCommand<
    IUpdateManyOperations,
    ICmdResult<IManyPlanningOrdersUpdatedResult>,
    typeof PlanningOrder
  >(Commands.UpdateManyOperations);

  static addOperation = collectionCommand<IAddOperation, PlanningOrder, typeof PlanningOrder>(
    Commands.AddOperation,
    { queryParams: { include: 'parent' } }
  );

  /**
   * Resource commands
   */

  updatePlanningOrder = command<IUpdatePlanningOrder, PlanningOrder>(Commands.UpdatePlanningOrder);
  launchOne = command<IChangeOneStatus, PlanningOrder>(Commands.LaunchOne, {
    query: { include: 'parent' }
  });
  draftOne = command<IChangeOneStatus, PlanningOrder>(Commands.DraftOne, {
    query: { include: 'parent' }
  });
  commitOne = command<IChangeOneStatus, PlanningOrder>(Commands.CommitOne, {
    query: { include: 'parent' }
  });
  sendOne = command<IChangeOneStatus, PlanningOrder>(Commands.SendOne, {
    query: { include: 'parent' }
  });
  finishOne = command<IChangeOneStatus, PlanningOrder>(Commands.FinishOne, {
    query: { include: 'parent' }
  });
  deleteOne = command<IPOCmd, PlanningOrder>(Commands.DeleteOne);
  split = command<IChangeOneStatus, PlanningOrder>(Commands.SplitOne);
  changeRoadmap = command<IChangeRoadmap, PlanningOrder>(Commands.ChangeRoadmap);
  acceptOneSupplierProposal = command<IPOCmd, PlanningOrder>(Commands.AcceptOneSupplierProposal);
  refuseOneSupplierProposal = command<IPOCmd, PlanningOrder>(Commands.RefuseOneSupplierProposal);
  sendOneSupplierProposal = command<IPOCmd, PlanningOrder>(Commands.SendOneSupplierProposal);
  // Operations
  updateOperation = command<IUpdateOperation, PlanningOrder>(Commands.UpdateOperation, {
    query: { include: 'parent' }
  });
  startOperation = command<IStartOperation, PlanningOrder>(Commands.StartOperation, {
    query: { include: 'parent' }
  });
  finishOperation = command<IFinishOperation, PlanningOrder>(Commands.FinishOperation, {
    query: { include: 'parent' }
  });
  toggleReschedulingLock = command<IToggleOperationReschedulingLock, PlanningOrder>(
    Commands.ToggleOperationReschedulingLock,
    {
      query: { include: 'parent' }
    }
  );
  removeOperation = command<IRemoveOperation, PlanningOrder>(Commands.RemoveOperation, {
    query: { include: 'parent' }
  });

  /**
   * Planning id
   */
  @attr('string') planningId?: string | null;
  @attr('string') groupId?: string | null;
  @attr('string') parentPoId?: string | null;

  /**
   * Reorder quantity
   */
  @attr('number') reorderQty!: number;

  /**
   * Due date
   */
  @attr('date') dueDate!: Date;

  /**
   * Reorder date (due date - dlt)
   */
  @attr('date') reorderDate!: Date;

  /**
   * Internal due date (due date - ilt)
   */
  @attr('date') deliveryDate!: Date;

  /**
   * Launch date (due date - lt)
   */
  @attr('date') launchDate!: Date;

  /**
   * Optimal Flow Date (Next stock out date - 1)
   */
  @attr('date') optimalFlowDate?: Date;

  /**
   *  acqCode Acquisition code
   */
  @attr('string') acqCode!: TAcquisitionCode;

  /**
   * Status
   */
  @attr('string') status!: TPOStatus;

  /**
   *  PO order type
   */
  @attr('string') orderType?: string | 'STANDARD';

  /**
   *  VOLTA generated order num
   */
  @attr('string') voltaOrderNum?: string;

  /**
   *  VOLTA generated order line
   */
  @attr('number') voltaOrderLine?: number;

  /**
   *  Internal order num
   */
  @attr('string') orderNum?: string;

  /**
   *  Internal order line
   */
  @attr('string') orderLine?: string;

  /**
   *  External order num
   */
  @attr('string') externalOrderNum?: string;

  /**
   * Overridden unit price
   */
  @attr('number') unitPrice?: number;

  /**
   * Workshop capacity production cadence
   */
  @attr('number') cadence?: number;

  /**
   * User id of the creator
   */
  @attr('string') createdBy?: string;

  /**
   * User id of the last updator
   */
  @attr('string') updatedBy?: string;

  @attr('string') validationStatus?: TPOValidationStatus;

  @attr('boolean') forceValidation?: boolean;

  @attr('number') priority?: number;

  @attr('string') currency?: string;

  @attr() initialValues?: TPOProposedValues;

  @attr() proposedValues?: TPOProposedValues;

  /**
   * Condensed Planning data
   */
  @attr() planningInfo!: { bufferPenetration?: number; bufferZone?: number };

  /**
   * Condensed Execution data
   */
  @attr() executionInfo!: { oha?: number; ohaZone?: number };

  /**
   * Condensed SKU data
   */
  @attr() skuInfo!: IPlanningOrderSKU;

  /**
   * Reorder quantity converted in multiple units
   */
  @attr() convertedQties!: Record<string, number>;

  /**
   * Aggregated stats like comments count
   */
  @attr() stats!: { commentsCount: number };

  /**
   * Condensed delivery Workshop data
   */
  @attr({
    defaultValue: () => {
      return {};
    }
  })
  deliveryWorkshopInfo?: IPOWorkshopInfo;

  /**
   * Condensed supplier data
   */
  @attr({
    defaultValue: () => {
      return {};
    }
  })
  supplierInfo?: ISupplierIdentityInfo;

  /**
   * Condensed workshop data
   */
  @attr({
    defaultValue: () => {
      return {};
    }
  })
  workshopInfo?: IPOWorkshopInfo;

  /**
   * Condensed alerts info
   */
  @attr() alerts!: { scheduling?: ISchedulingAlertSummary; synchronisation?: ISyncAlertSummary };

  /**
   * Aggregated data about each task type
   */
  @attr() tasks?: IAggTasksViewMap;

  /**
   * Custom fields values
   */
  @attr({
    defaultValue: () => ({})
  })
  customFields?: TCustomFieldValues;

  @attr('boolean') bypassCalendars!: boolean;
  @attr('array') supplierUserIds?: string[];
  @attr('string') timeBufferAlert?: TTimeBufferAlert;
  @attr('boolean', { defaultValue: false }) supplyAtDate!: boolean;

  /**
   * Operation related fields
   */
  @attr('boolean') isOperation!: boolean;
  @attr('string') routeId?: string;
  @attr('string') routeType?: TRouteType;
  @attr('number') operationNum?: number;
  @attr('number') operationPriority?: number;
  @attr('array') nextOperationNum?: number[];
  @attr('string') operationSkuId?: string;
  @attr('string') operationName?: string;
  @attr() operationCustomFields?: TCustomFieldValues;
  @attr('number', { defaultValue: 0 }) setupTime?: number;
  @attr('number', { defaultValue: 1 }) setupPcb?: number;
  @attr('number') processTime?: number;
  @attr('number', { defaultValue: 1 }) processPcb?: number;
  @attr('number', { defaultValue: 1 }) resourceQty?: number;
  @attr('number', { defaultValue: 0 }) waitTimeBefore?: number;
  @attr('number', { defaultValue: 0 }) waitTimeAfter?: number;
  @attr('number', { defaultValue: 0 }) transitTime?: number;
  // Kept as string to avoid timezone-dependent JS Date parsing.
  // They are warehouse-local datetimes and must be interpreted with warehouse config timezone.
  @attr('string') waitBeforeStart?: string;
  @attr('string') waitAfterEnd?: string;
  @attr('string') transitEnd?: string;
  @attr('number') estimatedProcessTime?: number;
  @attr('string') estimatedStartTime?: string;
  @attr('date') estimatedEndDate?: Date;
  @attr('string') estimatedEndTime?: string;
  @attr('date') startedAt?: Date;
  @attr('number', { defaultValue: 0 }) processCompletion?: number;
  @attr('date') launchedAt?: Date;
  @attr('date') committedAt?: Date;
  @attr('date') sentAt?: Date;
  @attr('date') finishedAt?: Date;
  @attr('date') cancelledAt?: Date;
  @attr('boolean', { defaultValue: false }) reschedulingDisabled!: boolean;

  /**
   * Sync fields
   * These fields are used to show the synchronisation status of the PO
   * with the external system (e.g. ERP)
   */

  @attr('string') syncStatus?: TPOSyncStatus;
  @attr('string') syncError?: string;
  @attr('string') syncAt?: Date;

  // Relationships
  // ~~~~
  @hasMany('planning-order', { async: false, inverse: null })
  operations?: PlanningOrder[];

  @belongsTo('planning-order', { async: false, inverse: null })
  parent?: PlanningOrder;

  // Tracked properties
  // ~~~~

  @tracked startPeriod?: string;

  // Computed Properties
  // ~~~~

  get validationStatusOption() {
    return this.validationStatus ? POValidationStatuses[this.validationStatus] : undefined;
  }

  get statusOption() {
    return POStatusOptions[this.status];
  }

  get targetStatusOption() {
    return this.proposedValues?.targetStatus && this.validationStatus === 'SUPPLIER_PROPOSAL'
      ? POStatusOptions[this.proposedValues?.targetStatus]
      : undefined;
  }

  get timeBufferAlertOption() {
    return this.timeBufferAlert ? TimeBufferAlerts[this.timeBufferAlert] : undefined;
  }

  get schedulingAlertOption() {
    return this.alerts.scheduling?.criticality
      ? SchedulingAlertOptions.find((o) => o.value === this.alerts.scheduling?.criticality)
      : undefined;
  }

  get syncAlertOption() {
    return this.alerts.synchronisation?.criticality
      ? SyncAlertOptions.find((o) => o.value === this.alerts.synchronisation?.criticality)
      : undefined;
  }

  get hasStatusChangeProposal() {
    const target = this.targetStatusOption?.value;
    return target && target !== this.status;
  }

  get dueDateOptimal() {
    return this.optimalFlowDate;
  }

  get reorderDateOptimal() {
    return this.optimalFlowDate
      ? moment.utc(this.optimalFlowDate).subtract(this.skuInfo.dlt, 'days').toDate()
      : undefined;
  }

  get deliveryDateOptimal() {
    return this.optimalFlowDate
      ? moment
          .utc(this.optimalFlowDate)
          .subtract(this.skuInfo.ilt ?? 0, 'days')
          .toDate()
      : undefined;
  }

  get launchDateOptimal() {
    return this.optimalFlowDate
      ? moment
          .utc(this.optimalFlowDate)
          .subtract(this.skuInfo.lt ?? 0, 'days')
          .toDate()
      : undefined;
  }

  get dueDateIsOptimal() {
    return moment.utc(this.effectiveDueDate).isSame(this.optimalFlowDate, 'days');
  }

  get stockOutDate() {
    return this.optimalFlowDate
      ? moment.utc(this.optimalFlowDate).add(1, 'days').toDate()
      : undefined;
  }

  get estimatedStartDateTime() {
    return `${formatISODate(this.launchDate)}T${this.estimatedStartTime ?? '00:00:00'}`;
  }

  get estimatedEndDateTime() {
    return `${formatISODate(this.estimatedEndDate ?? this.deliveryDate)}T${this.estimatedEndTime ?? '00:00:00'}`;
  }

  get warehouseTimeZone() {
    return (
      this.appSettings.byWarehouseOrDefault(this.warehouseId)?.settings?.timeZone ??
      this.appSettings.settings?.timeZone ??
      'UTC'
    );
  }

  get estimatedStartDateTimeValue() {
    return this.parseWarehouseLocalDateTime(this.estimatedStartDateTime);
  }

  get estimatedEndDateTimeValue() {
    return this.parseWarehouseLocalDateTime(this.estimatedEndDateTime);
  }

  get waitBeforeStartValue() {
    return this.parseWarehouseLocalDateTime(this.waitBeforeStart);
  }

  get waitAfterEndValue() {
    return this.parseWarehouseLocalDateTime(this.waitAfterEnd);
  }

  get transitEndValue() {
    return this.parseWarehouseLocalDateTime(this.transitEnd);
  }

  /**
   * Comments count
   */
  get commentsCount() {
    return this.tasks?.COMMENT?.total;
  }

  /**
   * Aggregated alerts count
   */
  get alertsCount() {
    const { scheduling, synchronisation } = this.alerts;
    const a = (parseNumber(scheduling?.criticality) as number) < 1;
    const b = (parseNumber(synchronisation?.criticality) as number) < 1;
    const c = this.timeBufferAlert === 'LATE' || this.timeBufferAlert === 'DANGER';

    return (a ? 1 : 0) + (b ? 1 : 0) + (c ? 1 : 0);
  }

  get isManaged() {
    const { methodology = 'NONE' } = this.skuInfo;
    return methodology !== 'NONE';
  }

  get isMake() {
    return this.acqCode === 'MAKE';
  }

  get isRoutedMakeOrder() {
    return this.isMake && Boolean(this.routeId) && !this.isOperation;
  }

  get isDueDateEditable() {
    return !this.isRoutedMakeOrder;
  }

  get effectiveDueDate() {
    return this.isRoutedMakeOrder ? (this.estimatedEndDate ?? this.dueDate) : this.dueDate;
  }

  /**
   * Sku id
   */
  get skuId() {
    return this.skuInfo.skuId;
  }

  /**
   * Delivery workshop id
   */
  get deliveryWorkshopId() {
    return this.deliveryWorkshopInfo?.workshopId;
  }

  /**
   * Supplier id
   */
  get supplierId() {
    return this.supplierInfo?.supplierId;
  }

  /**
   * Workshop id
   */
  get workshopId() {
    return this.workshopInfo?.workshopId;
  }

  /**
   * Warehouse id
   */
  get warehouseId() {
    return this.skuInfo.warehouse.warehouseId;
  }

  get partTypeId() {
    return this.skuInfo.partType?.partTypeId;
  }

  /**
   * Is the PO in DRAFT
   */
  get isDraft() {
    return this.status === 'DRAFT';
  }

  /**
   * Is the PO in LAUNCHED
   */
  get isLaunched() {
    return this.status === 'LAUNCHED';
  }

  /**
   * Is the PO in COMMITTED
   */
  get isCommitted() {
    return this.status === 'COMMITTED';
  }

  get isFirm() {
    return FirmPOStatuses.includes(this.status);
  }

  get operationStarted() {
    return this.isOperation && Boolean(this.startedAt);
  }

  get isOperationStartable() {
    return this.isOperation && this.isCommitted && !this.startedAt && this.isPrimaryOperation;
  }

  get isOperationFinishable() {
    return this.isOperation && this.isCommitted && this.startedAt && this.isPrimaryOperation;
  }

  get isOperationStartFixed() {
    return (
      this.isOperation &&
      (this.reschedulingDisabled || (this.isCommitted && this.startedAt) || this.sentAt)
    );
  }

  get canToggleReschedulingLock() {
    return this.isOperation && !this.startedAt && !this.sentAt && this.isPrimaryOperation;
  }

  /**
   * Planning priority name
   */
  get priorityName() {
    const { bufferZone } = this.planningInfo;
    return isPresent(bufferZone) ? getBufferZoneCriticality(bufferZone) : undefined;
  }

  /**
   * Execution priority name
   */
  get executionPriorityName() {
    const { ohaZone } = this.executionInfo;
    return isPresent(ohaZone) ? getBufferZoneCriticality(ohaZone) : undefined;
  }

  /**
   * Reorder quantity as a monetary value
   */
  get reorderQtyValue() {
    return this.reorderQty * (this.actualUnitPrice ?? 0);
  }

  get formattedUnitPrice() {
    return this.intl.formatNumber(this.actualUnitPrice, {
      style: 'currency',
      currency: this.currency ?? 'EUR'
    });
  }

  get formattedTotalPrice() {
    return this.intl.formatNumber(this.reorderQtyValue, {
      style: 'currency',
      currency: this.currency ?? 'EUR'
    });
  }

  /**
   * Actual order number
   */
  get num() {
    return this.orderNum ?? this.voltaOrderNum;
  }

  /**
   * Actual order line
   */
  get line() {
    return this.orderLine ?? this.voltaOrderLine;
  }

  /**
   * Is the PO a Distribution Order
   */
  get isDistribute() {
    return this.acqCode === 'DISTRIBUTE';
  }

  /**
   * Generated Key used for grouping PO as logical blocks
   */
  get orderKey(): string {
    const { num, warehouseId, acqCode, supplierId, deliveryWorkshopId, workshopId, isDistribute } =
      this;

    return `${num ?? noOrderNum}_${warehouseId}_${acqCode}_${supplierId ?? noId}_${
      workshopId ?? noId
    }_${(isDistribute && deliveryWorkshopId) ?? noId}`;
  }

  get orderHash() {
    return `${hashCode(this.orderKey)}`;
  }

  get displayName() {
    const { num, line } = this;
    return num && ` (${num}${line ? ` - ${line}` : ''})`;
  }

  get skuDisplayName() {
    const {
      skuInfo: { part },
      isOperation,
      operationNum,
      operationName
    } = this;
    return `#${isOperation ? operationNum : part.code} — ${isOperation ? operationName : part.description}`;
  }

  get partialParentPO() {
    const { parentPoId, skuId, skuInfo, reorderQty, status, reorderDate, dueDate, deliveryDate } =
      this;
    return parentPoId
      ? {
          id: parentPoId,
          skuId,
          skuInfo,
          reorderQty,
          deliveryDate,
          dueDate,
          reorderDate,
          status
        }
      : undefined;
  }

  /**
   * Object containing the grouping key with all its constituents
   */
  get orderKeyObject(): IPOKeyObject {
    return {
      orderHash: this.orderHash,
      orderKey: this.orderKey,
      num: this.num,
      warehouse: this.skuInfo.warehouse,
      acqCode: this.acqCode,
      supplier: this.supplierInfo,
      workshop: this.workshopInfo,
      deliveryWorkshop: this.isDistribute ? this.deliveryWorkshopInfo : undefined,
      externalOrderNum: this.externalOrderNum
    };
  }

  get actualUnitPrice() {
    return this.unitPrice ?? this.skuInfo.unitPrice;
  }

  get isSupplierDraft() {
    return this.validationStatus === 'SUPPLIER_DRAFT';
  }

  get isSupplierProposal() {
    return this.validationStatus === 'SUPPLIER_PROPOSAL';
  }

  get isSupplierProposalAccepted() {
    return this.validationStatus === 'CUSTOMER_APPROVAL';
  }

  get isSupplierProposalRefused() {
    return this.validationStatus === 'CUSTOMER_REFUSAL';
  }

  get supplierProposalCanBeSent() {
    return this.isSupplierDraft && this.status !== 'LAUNCHED';
  }

  get isTerminated() {
    const { status } = this;
    return status === 'CANCELLED' || status === 'FINISHED';
  }

  get isSupplierSplitDraft() {
    return Boolean(
      this.reorderQty === 0 &&
      (this.proposedValues?.reorderQty ?? 0) > 0 &&
      this.parentPoId &&
      this.isSupplierDraft
    );
  }

  get operationTotalDurationInHours() {
    if (this.sentAt && this.startedAt) {
      const start = moment.utc(this.startedAt);
      const end = moment.utc(this.sentAt);
      return end.diff(start, 'hours', true);
    }
    return this.estimatedProcessTime ?? 0;
  }

  get processTimeAsString() {
    return this.isMake ? formatDurationInHours(this.processTime) : undefined;
  }

  get estimatedProcessTimeAsString() {
    return this.isMake ? formatDurationInHours(this.estimatedProcessTime) : undefined;
  }

  get estimatedTotalTimeAsString() {
    return this.isMake ? formatDurationInHours(this.operationTotalDurationInHours) : undefined;
  }

  get resourceQtyFinal() {
    return this.resourceQty ?? 1;
  }

  get isPrimaryOperation() {
    return this.isOperation && this.operationPriority === 0;
  }

  get isSecondaryOperation() {
    return this.isOperation && this.operationPriority! > 0;
  }

  get workshopTypeOptions() {
    const { workshopTypes = [] } = this.workshopInfo || {};
    let options = [];
    for (let wt of workshopTypes) {
      const option = WorkshopTypes[wt];
      if (option) {
        options.push(option);
      }
    }
    return options;
  }

  /**
   * The route can be edited if the route is set and the PO is not already started
   */
  get isRouteEditable() {
    return (
      this.routeId &&
      this.acqCode === 'MAKE' &&
      !this.startedAt &&
      !this.sentAt &&
      !this.isOperation
    );
  }

  get unitMeasureAcq() {
    const {
      skuInfo: { supplierUnitMeasure, productionUnitMeasure, purchaseUnitMeasure, unitMeasure },
      acqCode
    } = this;

    let alt;

    if (acqCode === 'MAKE') {
      alt = productionUnitMeasure;
    } else if (acqCode === 'BUY') {
      alt = supplierUnitMeasure ?? purchaseUnitMeasure;
    }

    return alt === unitMeasure ? undefined : alt;
  }

  get reorderQtyAcq() {
    const { unitMeasureAcq, convertedQties } = this;
    if (!unitMeasureAcq) {
      return undefined;
    }
    return convertedQties[unitMeasureAcq];
  }

  get syncAlertDescription() {
    const po = this;
    const parts = [];

    const syncAlert = po.alerts.synchronisation?.payload;

    if (!syncAlert?.count) {
      return undefined;
    }

    parts.push(
      this.intl.t('poWorkbench.alerts.synchronisation.description', {
        count: syncAlert.count,
        launchDate: moment(po.launchDate).format('L')
      })
    );
    if (syncAlert.nextPossibleLaunchDate) {
      parts.push(
        this.intl.t('poWorkbench.alerts.synchronisation.nextLaunch', {
          launchDate: moment(syncAlert.nextPossibleLaunchDate).format('L')
        })
      );
    }
    return parts.join(' ');
  }

  get hasIdleTime() {
    if (!this.isOperation) {
      return false;
    }
    const waitBefore = this.waitTimeBefore ?? 0;
    const waitBeforeEnd = this.waitBeforeStartValue
      ? moment(this.waitBeforeStartValue).add(waitBefore * 3600000, 'milliseconds')
      : null;
    const processStart = this.estimatedStartDateTimeValue
      ? moment(this.estimatedStartDateTimeValue)
      : null;
    if (waitBeforeEnd && processStart?.isAfter(waitBeforeEnd, 'hour')) {
      return true;
    }
    return false;
  }

  private parseWarehouseLocalDateTime(value?: string) {
    if (!value) {
      return undefined;
    }
    const parsed = moment.tz(value, this.warehouseTimeZone);
    return parsed.isValid() ? parsed.toDate() : undefined;
  }

  search(term?: string, isSupplier?: boolean): boolean {
    const lcTerm = sanitized(term);

    if (!lcTerm || lcTerm === '') {
      return true; // If no term is provided, consider it a match
    }
    const { part, supplierSkuDescription } = this.skuInfo;
    const { code, description } = part;

    return (
      sanitized(this.num).includes(lcTerm) ||
      sanitized(this.operationName).includes(lcTerm) ||
      sanitized(code).includes(lcTerm) ||
      (!isSupplier && description ? sanitized(description).includes(lcTerm) : false) ||
      (isSupplier && supplierSkuDescription
        ? sanitized(supplierSkuDescription).includes(lcTerm)
        : false)
    );
  }

  getDateField = (field: TDateField) => {
    return (field === 'dueDate' ? this.effectiveDueDate : this[field]) as Date;
  };

  getFieldValue(field: TPOField | string) {
    if (field.includes('sku.customFields')) {
      return this.skuInfo.customFields?.[field.replace('sku.customFields.', '')];
    } else if (field.includes('operationCustomFields')) {
      return this.operationCustomFields?.[field.replace('operationCustomFields.', '')];
    } else if (field.includes('customFields')) {
      return this.customFields?.[field.replace('customFields.', '')];
    } else {
      return POFieldMapping[field as TPOField](this);
    }
  }

  [Type] = 'planning-order' as const;
}
